import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, signal, WritableSignal } from '@angular/core';
import { FormControl } from '@angular/forms';
import { form, FormField, required } from '@angular/forms/signals';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  PropertyDefinitionDto,
  PropertyOfferType,
  PropertyResidenceType,
  PropertySellerRole,
  PropertyType,
} from '@workspace/data-types';
import {
  AbstractSignalFormComponent,
  ButtonCardComponent,
  PhoneNumberInputComponent,
} from '@workspace/ng-aura';

export interface PropertySellerBeneficiaryModel {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  companyName: string;
  siren: string;
  estateName: string;
  notaryName: string;
}

export interface PropertySellerModel {
  role: PropertySellerRole;
  hasOwnerConsent: boolean;
  representationDetails: string;
  beneficiary: PropertySellerBeneficiaryModel;
}

export interface PropertyDefinitionModel {
  seller: PropertySellerModel;
  offerType: PropertyOfferType | null;
  propertyType: PropertyType | null;
  residenceType: PropertyResidenceType | null;
}

@Component({
  selector: 'etxe-property-definition-step',
  imports: [
    CommonModule,
    FormField,
    ButtonCardComponent,
    MatIcon,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    PhoneNumberInputComponent,
  ],
  templateUrl: './property-definition-step.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PropertyDefinitionStepComponent extends AbstractSignalFormComponent<
  PropertyDefinitionDto,
  PropertyDefinitionModel
> {
  phoneControl = new FormControl<string | null>('');
  PropertyType = PropertyType;
  PropertyOfferType = PropertyOfferType;
  PropertySellerRole = PropertySellerRole;
  PropertyResidenceType = PropertyResidenceType;

  sellerRoles = [
    {
      role: PropertySellerRole.OWNER,
      label: 'Je suis le propriétaire',
      icon: 'user-check',
      description: 'Le bien vous appartient en direct.',
    },
    {
      role: PropertySellerRole.FAMILY,
      label: 'J’aide un proche / mes parents',
      icon: 'users',
      description: 'Mandataire familial ou aidant.',
    },
    {
      role: PropertySellerRole.ESTATE_HEIR,
      label: 'Succession / Indivision',
      icon: 'landmark',
      description: 'Bien en succession ou partage familial.',
    },
    {
      role: PropertySellerRole.COMPANY,
      label: 'Société / SCI',
      icon: 'building-2',
      description: 'Bien détenu par une personne morale.',
    },
  ];

  offerTypes = [
    { type: PropertyOfferType.SALE, label: 'Vente', icon: 'euro' },
    {
      type: PropertyOfferType.LIFE_ANNUITY,
      label: 'Viager',
      icon: 'hourglass',
    },
    {
      type: PropertyOfferType.FORWARD_SALE,
      label: 'Vente à terme',
      icon: 'calendar-clock',
    },
  ];

  propertyTypes = [
    { type: PropertyType.HOUSE, label: 'Maison', icon: 'house' },
    { type: PropertyType.APARTMENT, label: 'Appartement', icon: 'building' },
    { type: PropertyType.LAND, label: 'Terrain', icon: 'land-plot' },
    {
      type: PropertyType.PARKING_BOX,
      label: 'Parking / Box',
      icon: 'circle-parking',
    },
    { type: PropertyType.BUSINESS, label: 'Fond de commerce', icon: 'store' },
    {
      type: PropertyType.PROFESSIONAL,
      label: 'Immobilier Pro',
      icon: 'building-2',
    },
  ];

  residenceTypes = [
    {
      type: PropertyResidenceType.MAIN,
      label: 'Résidence principale',
      icon: 'house',
      description: 'Votre résidence habituelle et effective.',
    },
    {
      type: PropertyResidenceType.SECONDARY,
      label: 'Résidence secondaire',
      icon: 'sun',
      description: 'Maison ou appartement de villégiature / vacances.',
    },
    {
      type: PropertyResidenceType.RENTAL_INVESTMENT,
      label: 'Investissement locatif',
      icon: 'trending-up',
      description: 'Bien acquis pour investissement ou loué.',
    },
  ];

  override model: WritableSignal<PropertyDefinitionModel> = signal<PropertyDefinitionModel>({
    seller: {
      role: PropertySellerRole.OWNER,
      hasOwnerConsent: true,
      representationDetails: '',
      beneficiary: {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        companyName: '',
        siren: '',
        estateName: '',
        notaryName: '',
      },
    },
    offerType: null,
    propertyType: null,
    residenceType: null,
  });

  override formTree = form(this.model, (b) => {
    required(b.seller.role);
    required(b.offerType);
    required(b.propertyType);
    required(b.seller.beneficiary.firstName, {
      when: () => this.model().seller.role === PropertySellerRole.FAMILY,
    });
    required(b.seller.beneficiary.lastName, {
      when: () => this.model().seller.role === PropertySellerRole.FAMILY,
    });
    required(b.seller.beneficiary.companyName, {
      when: () => this.model().seller.role === PropertySellerRole.COMPANY,
    });
    required(b.seller.beneficiary.estateName, {
      when: () => this.model().seller.role === PropertySellerRole.ESTATE_HEIR,
    });
    required(b.seller.hasOwnerConsent, {
      when: () => this.model().seller.role !== PropertySellerRole.OWNER,
    });
    required(b.residenceType, {
      when: () =>
        this.model().propertyType === PropertyType.HOUSE ||
        this.model().propertyType === PropertyType.APARTMENT,
    });
  });

  constructor() {
    super();
    effect(() => {
      const current = this.model().seller.beneficiary.phone;
      if (this.phoneControl.value !== current) {
        this.phoneControl.setValue(current || null, { emitEvent: false });
      }
    });
  }

  onPhoneChanged(newNumber: string) {
    this.model.update((m) => ({
      ...m,
      seller: {
        ...m.seller,
        beneficiary: {
          ...m.seller.beneficiary,
          phone: newNumber,
        },
      },
    }));
  }

  isNonOwner = computed(() => {
    const role = this.model().seller.role;
    return !!role && role !== PropertySellerRole.OWNER;
  });

  isFamily = computed(() => this.model().seller.role === PropertySellerRole.FAMILY);
  isCompany = computed(() => this.model().seller.role === PropertySellerRole.COMPANY);
  isEstateHeir = computed(() => this.model().seller.role === PropertySellerRole.ESTATE_HEIR);

  isResidential = computed(() => {
    const propType = this.model().propertyType;
    return propType === PropertyType.APARTMENT || propType === PropertyType.HOUSE;
  });

  selectSellerRole(role: PropertySellerRole) {
    this.model.update((m) => ({
      ...m,
      seller: {
        ...m.seller,
        role,
        hasOwnerConsent: role === PropertySellerRole.OWNER ? true : m.seller.hasOwnerConsent,
      },
    }));
  }

  selectType(type: PropertyType) {
    this.model.update((m) => ({
      ...m,
      propertyType: type,
      residenceType: type === PropertyType.APARTMENT || type === PropertyType.HOUSE ? m.residenceType : null,
    }));
  }

  selectResidenceType(type: PropertyResidenceType) {
    this.model.update((m) => ({ ...m, residenceType: type }));
  }

  selectOfferType(type: PropertyOfferType) {
    this.model.update((m) => ({ ...m, offerType: type }));
  }

  protected override buildModelFromDto(dto: PropertyDefinitionDto): PropertyDefinitionModel {
    return {
      seller: {
        role: dto?.seller?.role ?? PropertySellerRole.OWNER,
        hasOwnerConsent: dto?.seller?.hasOwnerConsent ?? true,
        representationDetails: dto?.seller?.representationDetails ?? '',
        beneficiary: {
          firstName: dto?.seller?.beneficiary?.firstName ?? '',
          lastName: dto?.seller?.beneficiary?.lastName ?? '',
          email: dto?.seller?.beneficiary?.email ?? '',
          phone: dto?.seller?.beneficiary?.phone ?? '',
          companyName: dto?.seller?.beneficiary?.companyName ?? '',
          siren: dto?.seller?.beneficiary?.siren ?? '',
          estateName: dto?.seller?.beneficiary?.estateName ?? '',
          notaryName: dto?.seller?.beneficiary?.notaryName ?? '',
        },
      },
      offerType: dto?.offerType ?? null,
      propertyType: dto?.propertyType ?? null,
      residenceType: dto?.residenceType ?? null,
    };
  }

  protected override buildDtoFromModel(model: PropertyDefinitionModel): PropertyDefinitionDto {
    const dto: PropertyDefinitionDto = {
      ...(this.dto() || ({} as PropertyDefinitionDto)),
      offerType: model.offerType ?? ('' as unknown as PropertyOfferType),
      propertyType: model.propertyType ?? ('' as unknown as PropertyType),
      residenceType: model.residenceType ?? undefined,
    };

    if (model.seller.role === PropertySellerRole.OWNER) {
      dto.seller = {
        role: PropertySellerRole.OWNER,
        hasOwnerConsent: true,
      };
    } else {
      const b = model.seller.beneficiary;
      const cleanedBeneficiary: Record<string, string> = {};
      (Object.keys(b) as (keyof typeof b)[]).forEach((k) => {
        const val = b[k];
        if (val && typeof val === 'string' && val.trim() !== '') {
          cleanedBeneficiary[k] = val.trim();
        }
      });

      dto.seller = {
        role: model.seller.role,
        hasOwnerConsent: model.seller.hasOwnerConsent,
        representationDetails: model.seller.representationDetails?.trim() || undefined,
        beneficiary:
          Object.keys(cleanedBeneficiary).length > 0
            ? (cleanedBeneficiary as unknown as PropertyDefinitionDto['seller']['beneficiary'])
            : undefined,
      };
    }

    return dto;
  }
}
